import type { Product } from 'payments-api/model';
import {
  useGetProductsSuspense,
  usePostProducts,
} from 'api/payments/generated/fragmentPaymentsAPI';
import clsx from 'clsx';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { FormButtons } from '@fragment/ui/src/components/Button/ButtonGroup/FormButtons';
import { ErrorText } from '@fragment/ui/src/components/ErrorText';
import { TextInput } from '@fragment/ui/src/components/Input/TextInput';

import { BillingToggle } from './BillingToggle';
import {
  ALL_ROLES_VALUE,
  FILLER_BORDER,
  ITEM_COLOR_CLASS,
  TILE_BORDER,
} from './constants';

const ProductTile = ({
  product,
  role,
  goToProduct,
}: {
  product: Product;
  role: string;
  goToProduct: (product: Product) => void;
}) => {
  const isPaidByRole = product.paid_by_roles.some((r) => r.name === role);
  const { price } = product as Product & { price?: string };
  return (
    <button
      type="button"
      onClick={() => goToProduct(product)}
      aria-label={`product-details-${product.code}`}
      data-testid="product-details-button"
      className={clsx(
        'flex flex-col items-start justify-start w-full p-f1 aspect-[8/9] overflow-hidden hover:bg-negative',
        TILE_BORDER
      )}
    >
      <div
        className={clsx('w-f2 h-f2 rounded-[2px] shrink-0', ITEM_COLOR_CLASS)}
      />
      <div className="mt-f1 w-full overflow-hidden">
        {product.description && (
          <span className="text-main text-xmd truncate w-full block text-left">
            {product.description}
          </span>
        )}
        <span className="text-main-500 text-xmd truncate w-full block text-left">
          {product.code}
        </span>
      </div>
      {price && <span className="mt-f2 text-main text-xmd">{price}</span>}
      {role !== ALL_ROLES_VALUE && (
        <span className="mt-f1 text-main-500 text-xmd">
          {isPaidByRole ? 'Paid By' : 'Paid To'} {role}
        </span>
      )}
    </button>
  );
};

const ProductListRow = ({
  product,
  role,
  goToProduct,
}: {
  product: Product;
  role: string;
  goToProduct: (product: Product) => void;
}) => {
  const isPaidByRole = product.paid_by_roles.some((r) => r.name === role);
  return (
    <button
      type="button"
      onClick={() => goToProduct(product)}
      aria-label={`product-details-${product.code}`}
      data-testid="product-details-button"
      className="flex items-center gap-x-f1 px-f1 py-f1 border-b-[0.5px] border-main-200 hover:bg-negative w-full text-left"
    >
      <div
        className={clsx('w-f2 h-f2 rounded-[2px] shrink-0', ITEM_COLOR_CLASS)}
      />
      <span className="text-main text-xmd flex-1 truncate">{product.code}</span>
      {product.description && (
        <span className="text-main-500 text-xmd flex-1 truncate">
          {product.description}
        </span>
      )}
      {role !== ALL_ROLES_VALUE && (
        <span className="text-main-500 text-xmd shrink-0">
          {isPaidByRole ? 'Paid By' : 'Paid To'}
        </span>
      )}
    </button>
  );
};

const DraftProductRow = ({
  selectedRoleName,
  onSubmit,
  onCancel,
  isPending,
}: {
  selectedRoleName: string;
  onSubmit: (
    code: string,
    description: string | undefined,
    isPurchase: boolean
  ) => Promise<void>;
  onCancel: () => void;
  isPending: boolean;
}) => {
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [isPurchase, setIsPurchase] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const handleSubmit = useCallback(async () => {
    setError(undefined);
    try {
      await onSubmit(code.trim(), description.trim() || undefined, isPurchase);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create product');
    }
  }, [code, description, isPurchase, onSubmit]);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      className="col-span-full flex flex-col gap-y-f1 w-full p-f1 min-h-f12 border-r-[0.5px] border-b-[0.5px] border-main-200"
    >
      <div className="flex flex-row items-center gap-x-f1 w-full">
        <TextInput
          padding="px-f1 py-f0"
          width="w-full"
          height="h-fit"
          align="left"
          placeholder="Product code"
          size="md"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          data-testid="draft-product-code"
        />
      </div>
      <TextInput
        padding="px-f1 py-f0"
        width="w-full"
        height="h-fit"
        align="left"
        placeholder="Description"
        size="md"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        data-testid="draft-product-description"
      />
      <div className="flex flex-row items-center gap-x-f1 w-full">
        <BillingToggle
          leftLabel="Paid By"
          rightLabel="Paid To"
          onLeftClick={() => setIsPurchase(true)}
          onRightClick={() => setIsPurchase(false)}
          initialActiveButton={isPurchase ? 'left' : 'right'}
          buttonPadding="px-f1 py-f0"
          buttonHeight="h-fit"
          data-testid="draft-product-paid-to-paid-by-toggle"
        />
        <span className="text-main text-xmd">{selectedRoleName}</span>
      </div>
      {error && <ErrorText>{error}</ErrorText>}
      <FormButtons
        type="secondary"
        cancelButtonLabel="Cancel"
        onCancel={onCancel}
        submitButtonLabel={isPending ? 'Creating...' : 'Create'}
        disabled={isPending || !code.trim()}
        submitButtonTestId="draft-product-submit-button"
        cancelButtonTestId="draft-product-cancel-button"
      />
    </form>
  );
};

type CatalogProductTableContentProps = {
  selectedRoleName: string;
  onProductClick: (product: Product) => void;
  viewMode: 'grid' | 'list';
  showDraftProduct: boolean;
  onDraftClose: () => void;
};

export const CatalogProductTableContent = ({
  selectedRoleName,
  onProductClick,
  viewMode,
  showDraftProduct,
  onDraftClose,
}: CatalogProductTableContentProps) => {
  const { data: productsData } = useGetProductsSuspense();
  const queryClient = useQueryClient();
  const createProductMutation = usePostProducts();

  const products: Product[] = productsData.data;
  const filteredProducts = products.filter(
    (product) =>
      selectedRoleName === ALL_ROLES_VALUE ||
      product.paid_by_roles.some((r) => r.name === selectedRoleName) ||
      product.paid_to_roles.some((r) => r.name === selectedRoleName)
  );

  const handleDraftProductSubmit = useCallback(
    async (
      code: string,
      description: string | undefined,
      isPurchase: boolean
    ) => {
      await createProductMutation.mutateAsync({
        data: {
          code,
          ...(description !== undefined && { description }),
          paid_to_roles: isPurchase ? [] : [{ name: selectedRoleName }],
          paid_by_roles: isPurchase ? [{ name: selectedRoleName }] : [],
        },
      });
      await queryClient.invalidateQueries({ queryKey: ['/products'] });
      onDraftClose();
    },
    [createProductMutation, selectedRoleName, queryClient, onDraftClose]
  );

  if (viewMode === 'list') {
    return (
      <div className="flex flex-col w-full">
        {filteredProducts.map((product) => (
          <ProductListRow
            key={product.id}
            product={product}
            role={selectedRoleName}
            goToProduct={onProductClick}
          />
        ))}
        {showDraftProduct && (
          <DraftProductRow
            selectedRoleName={selectedRoleName}
            onSubmit={handleDraftProductSubmit}
            onCancel={onDraftClose}
            isPending={createProductMutation.isPending}
          />
        )}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] w-full min-h-[360px]">
      {filteredProducts.map((product) => (
        <ProductTile
          key={product.id}
          product={product}
          role={selectedRoleName}
          goToProduct={onProductClick}
        />
      ))}
      {showDraftProduct && (
        <DraftProductRow
          selectedRoleName={selectedRoleName}
          onSubmit={handleDraftProductSubmit}
          onCancel={onDraftClose}
          isPending={createProductMutation.isPending}
        />
      )}
      {Array.from({ length: 8 }).map((_, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={`filler-${i}`} className={FILLER_BORDER} />
      ))}
    </div>
  );
};

type CatalogProductTableProps = {
  selectedRoleName: string;
  onProductClick: (product: Product) => void;
  viewMode: 'grid' | 'list';
  showDraftProduct: boolean;
  onDraftClose: () => void;
};

const SKELETON_MAX_TILES = 12;

const CatalogProductTableLoading = () => {
  const [visibleTiles, setVisibleTiles] = useState(0);
  const directionRef = useRef(1);

  useEffect(() => {
    const timer = setInterval(() => {
      setVisibleTiles((prev) => {
        const next = prev + directionRef.current;
        if (next >= SKELETON_MAX_TILES) {
          directionRef.current = -1;
          return SKELETON_MAX_TILES;
        }
        if (next <= 0) {
          directionRef.current = 1;
          return 0;
        }
        return next;
      });
    }, 120);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] w-full min-h-[360px]">
      <div className={clsx('aspect-[8/9]', TILE_BORDER)} />
      {/* eslint-disable react/no-array-index-key */}
      {Array.from({ length: visibleTiles }).map((_, i) => (
        <div
          key={`skeleton-${i}`}
          className={clsx('aspect-[8/9]', TILE_BORDER)}
        />
      ))}
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={`filler-${i}`} className={FILLER_BORDER} />
      ))}
      {/* eslint-enable react/no-array-index-key */}
    </div>
  );
};

export const CatalogProductTable = ({
  selectedRoleName,
  onProductClick,
  viewMode,
  showDraftProduct,
  onDraftClose,
}: CatalogProductTableProps): JSX.Element => (
  <Suspense fallback={<CatalogProductTableLoading />}>
    <CatalogProductTableContent
      selectedRoleName={selectedRoleName}
      onProductClick={onProductClick}
      viewMode={viewMode}
      showDraftProduct={showDraftProduct}
      onDraftClose={onDraftClose}
    />
  </Suspense>
);
